import { Component, Mixins } from "vue-property-decorator";
import ContactInfo from "@/dvd-orders/components/ContactInfo.vue";
import { DvdOrderCheckoutParameters, DvdOrderDetails, DvdReceiptParameters, State } from "@/dvd-orders/DvdModel";
import CancelMixin from "@/dvd-orders/mixins/CancelMixin";
import { Page } from "@/router";
import { calculateTaxes, submitOrderToAPI, verifyOrderPaid } from "@/dvd-orders/DvdOrderService";
import { Store } from "vuex";
import CartItems from "@/dvd-orders/components/CartItems.vue";
import DvdSteps from "@/dvd-orders/components/DvdSteps.vue";
import BigNumber from "bignumber.js";
import { HTTP } from "@/services/HttpServices";
import MessageModal from "./MessageModal.vue";

@Component({
  components: {
    ContactInfo,
    CartItems,
    DvdSteps,
    MessageModal,
  },
  filters: {
    stateName(stateId: string, store: Store<any>): string {
      const states = store.state ? store.state.states : [];
      if (states && states.length > 0) {
        const state = states.filter((state: State) => state.id === stateId).pop();
        if (state) {
          return state.name;
        }
      }
      return "";
    },
  },
})
export default class DvdReview extends Mixins(CancelMixin) {
  isLoading = false;
  private checkoutParameters: DvdOrderCheckoutParameters | null = null;
  private clover: any = null;

  get orderDetails(): DvdOrderDetails {
    return Object.assign({}, this.$store.state.dvdOrderDetails);
  }

  get billingStateName(): string {
    return this.$options.filters!.stateName(this.$store, this.orderDetails.billingInfo.stateId);
  }

  async created() {
    await Promise.all([this.$store.dispatch("loadStates"), this.getTaxInfo()]);
  }

  async mounted() {
    await this.loadScript("https://www.google.com/recaptcha/api.js?render=6Lf6zREpAAAAAMHA4v3x6rqqSo1w5ga7az4i__p0");
    await this.initClover();
    // @ts-ignore
    window.submitPayment = this.submitPayment;
  }

  async submitPayment(): Promise<void> {
    try {
      const isValid = await this.verifyCaptcha();
      if (!isValid) {
        throw Error('Invalid Captcha')
      }
    } catch (_) {
      //@ts-ignore
      return this.$refs.modal.openModal(
        "Oops! It seems there was an issue verifying your access. Please try again later or contact support for assistance."
      );
    }

    this.isLoading = true;
    //  Save billing and shipping information to the server
    try {
      this.checkoutParameters = await submitOrderToAPI(this.orderDetails);
      this.$store.commit("orderSubmitted", this.checkoutParameters!.confirmationNumber);
    } catch (err) {
      this.isLoading = false;
      return this.$notifyError("Unable to submit order please try again later.");
    }

    //  Tokenize the card
    let result = null;
    try {
      result = await this.clover.createToken();
      if (result.errors) throw Error("Clover Errors");
    } catch (err) {
      this.isLoading = false;
      return this.$notifyError("Unable to tokenize card please try again later.");
    }

    //  Send the token to the Bek API to be charged
    try {
      const name = `${this.orderDetails.billingInfo.firstName} ${this.orderDetails.billingInfo.lastName}`;
      const amount = Math.round(parseFloat(this.orderDetails.totalAmount) * 100);
      const isSuccessful = await this.chargePayment(amount, result.token, name);
      if (!isSuccessful) throw Error("Charge Error");
    } catch (err) {
      this.isLoading = false;
      return this.$notifyError("Unable to charge card please try again later.");
    }

    // Mark the BEK order as completed, it will then show in the admin dashboard
    try {
      const receipt: DvdReceiptParameters = {
        id: 0,
        last4: result.card.last4,
        cardType: result.card.brand,
        billingEmail: this.orderDetails.billingInfo.email,
        confirmationNumber: this.checkoutParameters.confirmationNumber,
        payeezySuccess: true,
      };
      const verifyResponse = await verifyOrderPaid(receipt);
      if (!verifyResponse.success) throw Error("Verification Error");
      this.isLoading = false;
      //@ts-ignore
      this.$refs.modal.openModal(
        `Thanks for your order! For your records your confirmation number is ${this.checkoutParameters?.confirmationNumber}. A receipt will be emailed to you shortly.`,
        this.handleClose
      );
    } catch (err) {
      this.isLoading = false;
      //@ts-ignore
      return this.$refs.modal.openModal(
        "Unable to verify payment. If you successfully paid, please contact BEK Support. We apologize for any inconvenience.",
        this.handleClose
      );
    }
  }

  private async verifyCaptcha(): Promise<boolean> {
    // @ts-ignore
    const token = await grecaptcha.execute("6Lf6zREpAAAAAMHA4v3x6rqqSo1w5ga7az4i__p0", { action: "submit" });
    const response = await HTTP.post(`/public/dvd-orders/captcha`, {
      response: token,
    });
    return response.isValid;
  }

  private async chargePayment(amount: number, token: string, name: string): Promise<boolean> {
    const response = await HTTP.post(`/public/dvd-orders/charge`, {
      description: name,
      amount: amount,
      source: token,
    });
    return response.success;
  }

  private async initClover(): Promise<void> {
    const isProduction = ["https://www.bek.tv", "https://tv.stagebek.com"].includes(window.location.origin);
    const SDK_URL = isProduction
      ? "https://checkout.clover.com/sdk.js"
      : "https://checkout.sandbox.dev.clover.com/sdk.js";
    const CLOVER_PUBLIC_KEY = isProduction ? "b573c420611bba93603e6f1d538de2fa" : "690b4bfb4313bd9fdadbc635fea8da58";
    await this.loadScript(SDK_URL);

    // Link Clover checkout SDK
    // @ts-ignore
    this.clover = new Clover(CLOVER_PUBLIC_KEY);
    const elements = this.clover.elements();

    // Create card elements
    const cardNumber = elements.create("CARD_NUMBER");
    const cardDate = elements.create("CARD_DATE");
    const cardCvv = elements.create("CARD_CVV");
    const cardPostalCode = elements.create("CARD_POSTAL_CODE");

    // Mount each element
    cardNumber.mount("#card-number");
    cardDate.mount("#card-date");
    cardCvv.mount("#card-cvv");
    cardPostalCode.mount("#card-postal-code");

    // Get card response element id
    const cardResponse = document.getElementById("card-response");

    // Get submit button element id
    const submit_button = document.getElementById("submit_button");

    // Get card errors by element id
    const displayCardNumberError: HTMLElement = document.getElementById("card-number-errors")!;
    const displayCardDateError: HTMLElement = document.getElementById("card-date-errors")!;
    const displayCardCvvError: HTMLElement = document.getElementById("card-cvv-errors")!;
    const displayCardPostalCodeError: HTMLElement = document.getElementById("card-postal-code-errors")!;

    /* Event Listeners */
    cardNumber.addEventListener("change", (event: any) => {
      displayCardNumberError.innerHTML = event.CARD_NUMBER.error || "";
    });
    cardNumber.addEventListener("blur", (event: any) => {
      displayCardNumberError.innerHTML = event.CARD_NUMBER.error || "";
    });
    cardDate.addEventListener("change", (event: any) => {
      displayCardDateError.innerHTML = event.CARD_DATE.error || "";
    });
    cardDate.addEventListener("blur", (event: any) => {
      displayCardDateError.innerHTML = event.CARD_DATE.error || "";
    });
    cardCvv.addEventListener("change", (event: any) => {
      displayCardCvvError.innerHTML = event.CARD_CVV.error || "";
    });
    cardCvv.addEventListener("blur", (event: any) => {
      displayCardCvvError.innerHTML = event.CARD_CVV.error || "";
    });
    cardPostalCode.addEventListener("change", (event: any) => {
      displayCardPostalCodeError.innerHTML = event.CARD_POSTAL_CODE.error || "";
    });
    cardPostalCode.addEventListener("blur", (event: any) => {
      displayCardPostalCodeError.innerHTML = event.CARD_POSTAL_CODE.error || "";
    });
  }

  loadScript(url: string) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = url;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  handleClose() {
    this.$store.commit("clearCart");
    this.$router.push({ name: Page.DVD_ORDER });
  }

  async getTaxInfo() {
    if (new BigNumber(this.orderDetails.taxAmount).eq(0)) {
      try {
        const results = await calculateTaxes(this.orderDetails);
        this.$store.commit("updateTaxes", results.taxAmount);
      } catch (e) {
        this.$notifyError("Failed to calculate taxes.");
      }
    }
  }

  returnClicked() {
    this.$router.push({ name: Page.CHECKOUT_INFO }).catch(() => {});
  }
}
